import React, { useState } from 'react';
import { Slider } from '@wajeez-web/components/ui';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';
import { SeeMoreModal } from '../modals';
import { Skeleton } from 'antd';
import { MediaHelpers } from '@wajeez-web/core/api';
import { useNavigate } from 'react-router-dom';

export interface LargeRectangleListParameters {
  title: string | null;
  seeAllTitle: string | null;
}

export function LargeRectangleList({
  component,
  context,
  isLastSibling,
  pageSize,
  pageDataId,
  pageDataContextId,
  seeMoreType = 'curatedlist',
}: BaseComponentProps) {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getParameterValue, contents, contentsRequestStatus } =
    useComponentData<LargeRectangleListParameters>(
      component,
      context,
      undefined,
      pageSize
    );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  const seeAllTitle = getParameterValue('seeAllTitle');
  const title = getParameterValue('title');

  return (
    <div className="space-y-[17px]">
      {contentsRequestStatus !== 'loading' &&
      contentsRequestStatus !== 'failed' &&
      contents?.length ? (
        <div className="w-full space-y-[17px] sm:space-y-[7px]">
          <div className="flex justify-between px-4 sm:px-0">
            <div className="font-bold text-[20px] sm:text-[18px] sm:leading-[30px] sm:text-[#27160E]">
              {title}
            </div>
            {seeAllTitle && (
              <>
                <div
                  className="sm:hidden text-[17px]"
                  onClick={() => setIsModalOpen(true)}
                >
                  {seeAllTitle.trim() ? seeAllTitle.trim() : 'عرض الكل'}
                </div>
                <div
                  className="hidden sm:block h-[33px] -translate-y-[6px] pt-[7px] pb-[8px] px-[18px] bg-[#F2F3F5] rounded-[21px] text-[15px] leading-[18px] font-medium text-black cursor-pointer"
                  onClick={() => {
                    if (!pageDataContextId) {
                      navigate(
                        `/content/see-more?pageDataId=${pageDataId}&componentId=${component?.id}`
                      );
                    } else {
                      navigate(
                        `/content/${seeMoreType}/see-more?pageDataId=${pageDataId}&pageDataContextId=${pageDataContextId}&componentId=${component?.id}&contextId=${context?.id}`
                      );
                    }
                  }}
                >
                  {seeAllTitle.trim() ? seeAllTitle.trim() : 'عرض الكل'}
                </div>
              </>
            )}
          </div>
          <div>
            <div
              className={`w-full sm:pb-[28px] ${
                !isLastSibling && 'border-b-[0.5px]'
              } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider`}
            >
              <LargeRectangleSlider>
                {contents?.slice(0, pageSize)?.map((content, i) => (
                  <RepeatedCuratedListGridItem
                    key={content.id}
                    component={{ ...component?.itemTemplate, uniqueId: i }}
                    context={content}
                  />
                ))}
              </LargeRectangleSlider>
            </div>
          </div>
        </div>
      ) : (contentsRequestStatus === 'loading' ||
          contentsRequestStatus === 'failed') &&
        !contents?.length ? (
        <LargeRectangleListSkeleton
          isLastSibling={isLastSibling}
          isActive={isActive}
        />
      ) : null}
      {isModalOpen && (
        <SeeMoreModal
          title={title}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          component={component}
          context={context}
          type={component?.type}
        />
      )}
    </div>
  );
}

const LargeRectangleListSkeleton = ({
  isLastSibling,
  isActive = true,
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
}) => {
  return (
    <div className="w-full pr-2 sm:pr-0">
      <div className="pr-2 sm:pr-0">
        <Skeleton.Button
          active={isActive}
          shape="square"
          className="!w-[200px] !h-[30px] dark:bg-dark-medium"
        />
      </div>
      <div
        className={`mt-3 w-full ${
          !isLastSibling && 'border-b-[0.5px]'
        } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider sm:border-none`}
      >
        <LargeRectangleSlider>
          {Array.from({ length: 10 }).map((_, i) => {
            return (
              <div key={i} className="pl-[20px] sm:pl-[80px]">
                <Skeleton.Node
                  active={isActive}
                  className="!w-[249px] !h-[134px] dark:bg-dark-medium"
                >
                  <div />
                </Skeleton.Node>
              </div>
            );
          })}
        </LargeRectangleSlider>
      </div>
    </div>
  );
};

export interface LargeRectangleGridItemParameters {
  image: string | null;
  category: string | null;
}

const RepeatedCuratedListGridItem: React.FC<BaseComponentProps> = ({
  component,
  context,
}) => {
  const { getParameterValue, content } =
    useComponentData<LargeRectangleGridItemParameters>(component, context);

  const image = getParameterValue('image');

  return (
    <div className="pl-[20px] sm:pl-[60px] sm:pr-[20px]">
      <ClickableComponentLayout content={content}>
        <div className="relative w-[249px] h-[134px] bg-[#E3E3E3] dark:bg-[#373737] rounded-[19px]">
          <div className="w-[249px] h-[134px] absolute top-[-6px] left-[6px] bg-[#F0F0F0] dark:bg-[#5E5E5E] rounded-[15px]">
            <div
              className="w-[249px] h-[134px] absolute top-[-6px] left-[6px] rounded-[15px]"
              style={{
                backgroundImage:
                  'url(' + MediaHelpers.getPublicMediaById(image) + ')',
                backgroundSize: 'cover',
              }}
            >
              <div className="w-[225px] h-[110px] absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 rounded-[3px] bg-[#FFFFFF2E] flex justify-center items-center">
                <h1 className="max-w-[182px] text-[18px] sm:text-[20px] font-bold text-white text-center">
                  {content?.title}
                </h1>
              </div>
            </div>
          </div>
        </div>
        <h2 className="pt-3 sm:pt-[18px] text-[14px] sm:text-[24px] font-medium text-[#8B8A8F] sm:text-[#878989]">
          {content?.chaptersCount}{' '}
          {content?.chaptersCount === 1 ? 'عنوان' : 'عناوين'}
        </h2>
      </ClickableComponentLayout>
    </div>
  );
};

interface LargeRectangleSliderProps {
  children: React.ReactNode;
}

const LargeRectangleSlider: React.FC<LargeRectangleSliderProps> = ({
  children,
}) => {
  return (
    <Slider
      // arrows={false}
      dots={true}
      responsive={{
        desktop: {
          breakpoint: {
            max: 3000,
            min: 1337,
          },
          items: 4,
        },
        miniDesktop: {
          breakpoint: {
            max: 1336,
            min: 1024,
          },
          items: 3,
        },
        mobile: {
          breakpoint: {
            max: 464,
            min: 0,
          },
          items: 1,
        },
        tablet: {
          breakpoint: {
            max: 1024,
            min: 464,
          },
          items: 2,
        },
      }}
      customStyle="pt-[12px] sm:pt-[20px] pb-[40px] sm:pb-0 pr-[12px] sm:mr-[-20px] sm:max-w-[930px] desktop:max-w-[1200px]"
    >
      {children}
    </Slider>
  );
};
